import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { User, Clock, CheckCircle, Calendar, DollarSign, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import ComparisonModal from './ComparisonModal';
import ConfirmationModal from './ConfirmationModal';
import { AnimatePresence } from 'framer-motion';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";

const COLUMNS = [
    { id: 'applied', title: 'Applied', color: 'bg-slate-100 dark:bg-slate-800', border: 'border-slate-200 dark:border-slate-700', icon: Clock },
    { id: 'screening', title: 'Screening', color: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', icon: User },
    { id: 'interview', title: 'Interview', color: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800', icon: Calendar },
    { id: 'offer', title: 'Offer', color: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', icon: DollarSign },
    { id: 'hired', title: 'Hired', color: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', icon: CheckCircle },
];

export default function KanbanBoard({ onPreview, jobId = null }) {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchCandidates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    const fetchCandidates = async () => {
        setLoading(true);
        try {
            if (jobId) {
                // Fetch real applications
                const res = await axios.get(`${API_BASE}/jobs/${jobId}/candidates`);
                // Map application response to expected shape
                const mapped = res.data.map(app => ({
                    ...app.candidate,
                    _id: app.resume_id, // Ensure resume details are top level for card
                    application_id: app._id,
                    status: app.status,
                    notes: app.notes,
                    fitReason: app.ai_justification
                }));
                setCandidates(mapped);
            } else {
                // Global view (Legacy/Mock)
                const res = await axios.get(`${API_BASE}/resumes?limit=50`);
                const distributedParam = res.data.map((c, i) => ({
                    ...c,
                    status: c.status || (i % 5 === 0 ? 'screening' : 'applied') // Prefer DB status if exists
                }));
                setCandidates(distributedParam);
            }
        } catch (error) {
            console.error("Failed to fetch candidates", error);
            toast.error("Could not load pipeline");
        } finally {
            setLoading(false);
        }
    };

    const moveCandidate = async (candidate, newStatus) => {
        // Optimistic update
        setCandidates(prev => prev.map(c =>
            (c._id === candidate._id) ? { ...c, status: newStatus } : c
        ));

        if (jobId && candidate.application_id) {
            // Real update
            try {
                await axios.patch(`${API_BASE}/applications/${candidate.application_id}/status?status=${newStatus}`);
                toast.success(`Moved to ${newStatus}`);
            } catch (err) {
                console.error(err);
                toast.error("Failed to update status");
                fetchCandidates(); // Revert on fail
            }
        }
    };

    const handleDragStart = (e, candidate) => {
        e.dataTransfer.setData("candidateId", candidate._id || candidate.resume_id);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e, status) => {
        const id = e.dataTransfer.getData("candidateId");
        const candidate = candidates.find(c => (c._id === id || c.resume_id === id));
        if (candidate) {
            moveCandidate(candidate, status);
        }
    };

    const [selectedCandidates, setSelectedCandidates] = useState(new Set());
    const [isComparing, setIsComparing] = useState(false);
    const [comparisonData, setComparisonData] = useState(null);

    const toggleSelection = (id) => {
        const newSet = new Set(selectedCandidates);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedCandidates(newSet);
    };

    const handleCompare = async () => {
        if (selectedCandidates.size < 2) return;
        setIsComparing(true);
        try {
            const res = await axios.post(`${API_BASE}/compare`, {
                job_id: jobId,
                candidate_ids: Array.from(selectedCandidates)
            });
            setComparisonData(res.data);
        } catch (err) {
            console.error(err);
            toast.error("Comparison failed");
            setIsComparing(false); // Close on error
        }
    };

    const [deleteCandidate, setDeleteCandidate] = useState(null);

    const handleDeleteClick = (candidate, e) => {
        e.stopPropagation();
        setDeleteCandidate(candidate);
    };

    const confirmDelete = async () => {
        if (!deleteCandidate) return;
        const candidate = deleteCandidate;

        // Optimistic update
        setCandidates(prev => prev.filter(c => c._id !== candidate._id));

        if (jobId && candidate.application_id) {
            try {
                await axios.delete(`${API_BASE}/applications/${candidate.application_id}`);
                toast.success("Candidate removed");
            } catch (err) {
                console.error(err);
                toast.error("Failed to remove candidate");
                fetchCandidates(); // Revert
            }
        }
        setDeleteCandidate(null);
    };

    if (loading) {
        return (
            <div className="flex h-[calc(100vh-10rem)] items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            <div className="mb-6 px-2 text-center">
                <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-brand-700 to-purple-600 dark:from-brand-400 dark:to-purple-300">
                    Pipeline
                </h2>
                <p className="text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
                    Drag and drop candidates to manage progress
                </p>
            </div>
            <div className="h-[calc(100vh-11rem)] overflow-x-auto pb-4 snap-x snap-mandatory px-4">
                <div className="flex gap-6 min-w-max h-full">
                    {COLUMNS.map(col => (
                        <div
                            key={col.id}
                            className="w-72 flex flex-col shrink-0 snap-center"
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, col.id)}
                        >
                            <div className={clsx(
                                "flex flex-col h-full rounded-xl border overflow-hidden transition-colors",
                                col.color.replace('bg-', 'bg-opacity-30 '),
                                col.border
                            )}>
                                {/* Column Header */}
                                <div className={clsx(
                                    "flex items-center justify-between p-3 border-b bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm",
                                    col.border
                                )}>
                                    <div className="flex items-center gap-2">
                                        <div className={clsx("p-1.5 rounded-lg bg-white dark:bg-slate-700 shadow-sm", col.color)}>
                                            <col.icon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                                        </div>
                                        <span className="font-bold text-slate-700 dark:text-slate-200 text-sm">{col.title}</span>
                                    </div>
                                    <span className="bg-white dark:bg-slate-700 px-2 py-0.5 rounded-full text-xs font-semibold text-slate-600 dark:text-slate-300 shadow-sm border border-slate-100 dark:border-slate-600">
                                        {candidates.filter(c => c.status === col.id).length}
                                    </span>
                                </div>

                                {/* Drop Zone / List */}
                                <div className="flex-1 p-3 space-y-3 overflow-y-auto no-scrollbar pb-10">
                                    {candidates
                                        .filter(c => c.status === col.id)
                                        .map((candidate) => (
                                            <KanbanCard
                                                key={candidate._id || candidate.resume_id}
                                                candidate={candidate}
                                                onMove={moveCandidate}
                                                onPreview={() => onPreview(candidate._id || candidate.resume_id, candidate.name, candidate)}
                                                onDragStart={(e) => handleDragStart(e, candidate)}
                                                onDelete={(e) => handleDeleteClick(candidate, e)}
                                                isSelected={selectedCandidates.has(candidate._id)}
                                                onSelect={() => toggleSelection(candidate._id)}
                                            />
                                        ))
                                    }
                                    {candidates.filter(c => c.status === col.id).length === 0 && (
                                        <div className="text-center py-10 text-slate-400 text-xs italic opacity-50">
                                            Drop here
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {/* Floating Compare Button */}
            <AnimatePresence>
                {selectedCandidates.size >= 2 && (
                    <motion.div
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40"
                    >
                        <button
                            onClick={handleCompare}
                            className="bg-slate-900 text-white px-6 py-3 rounded-full font-bold shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-3 border border-slate-700"
                        >
                            <span className="bg-brand-500 text-white text-xs px-2 py-0.5 rounded-full">{selectedCandidates.size}</span>
                            Compare Candidates
                            {isComparing && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            <ComparisonModal
                isOpen={!!comparisonData}
                onClose={() => { setComparisonData(null); setIsComparing(false); }}
                data={comparisonData}
                candidates={candidates.filter(c => selectedCandidates.has(c._id))}
            />

            <ConfirmationModal
                isOpen={!!deleteCandidate}
                onClose={() => setDeleteCandidate(null)}
                onConfirm={confirmDelete}
                title="Remove Candidate?"
                message={`Are you sure you want to remove ${deleteCandidate?.name} from this pipeline? They will still remain in the Talent Vault.`}
                confirmText="Remove Candidate"
            />
        </div>
    );
}

function KanbanCard({ candidate, onMove, onPreview, onDragStart, onDelete, isSelected, onSelect }) {
    return (
        <motion.div
            layout
            draggable
            onDragStart={onDragStart}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            whileDrag={{ scale: 1.05, rotate: 2, zIndex: 10 }}
            className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 hover:shadow-md transition-all group relative cursor-grab active:cursor-grabbing"
        >
            <div className="flex justify-between items-start mb-2">
                <div className="pt-1 pr-2">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => { e.stopPropagation(); onSelect(); }}
                        className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                    />
                </div>
                <div
                    onClick={onPreview}
                    className="flex items-center gap-3 cursor-pointer flex-1"
                >
                    <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/50 flex items-center justify-center text-brand-600 dark:text-brand-400 font-bold text-xs shrink-0">
                        {candidate.name ? candidate.name.substring(0, 2).toUpperCase() : '??'}
                    </div>
                    <div className="min-w-0">
                        <h4 className="font-semibold text-sm text-slate-800 dark:text-slate-100 truncate">
                            {candidate.name || 'Unknown'}
                        </h4>
                        <p className="text-[10px] text-slate-400 truncate">{candidate.email}</p>
                    </div>
                </div>
                <button
                    onClick={onDelete}
                    className="text-slate-300 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>

            <div className="space-y-2">
                <div className="flex flex-wrap gap-1">
                    <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded border border-slate-200 dark:border-slate-600">
                        {candidate.years_experience}y Exp
                    </span>
                    {candidate.skills && candidate.skills.slice(0, 2).map((s, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded border border-slate-200 dark:border-slate-600 truncate max-w-[60px]">
                            {s}
                        </span>
                    ))}
                </div>

                {candidate.fitReason && (
                    <div className="mt-2 p-2 bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-800/50 rounded-lg">
                        <p className="text-[9px] text-purple-700 dark:text-purple-300 italic line-clamp-2 leading-relaxed">
                            ✨ {candidate.fitReason}
                        </p>
                    </div>
                )}
            </div>

            {/* Quick Actions (Hover) */}
            <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-white via-white to-transparent dark:from-slate-800 dark:via-slate-800 opacity-0 group-hover:opacity-100 transition-opacity flex justify-between gap-1">
                <button
                    onClick={() => onMove(candidate, getNextStatus(candidate.status))}
                    className="flex-1 bg-brand-600 hover:bg-brand-700 text-white text-xs py-1.5 rounded-lg font-medium shadow-sm"
                >
                    Advance
                </button>
            </div>
        </motion.div>
    );
}

function getNextStatus(current) {
    const sequence = ['applied', 'screening', 'interview', 'offer', 'hired'];
    const idx = sequence.indexOf(current);
    if (idx === -1 || idx === sequence.length - 1) return current;
    return sequence[idx + 1];
}
